import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DropdownItemsList } from 'components/dropdown/Dropdown';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useHighlightedIndex } from 'custom_hooks/useHighlightedIndex';
import { useKeyPress } from 'custom_hooks/useKeyPress';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import {
    ReactNode,
    Ref,
    RefCallback,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { stopEvent } from 'utils/event';

export type HighlightedIndex = number | 'after';

export const defaultPopupMaxHeight = '60vh';
const popupBottomPadding = '2rem';

const getPopupAlign = (
    triggerElem: HTMLElement | null,
    windowInnerWidth: number
): 'left' | 'right' => {
    if (!triggerElem) {
        return 'right';
    }

    const windowMidX = Math.floor(windowInnerWidth / 2);
    const { left, right } = triggerElem.getBoundingClientRect();
    const triggerMidX = (left + right) / 2;

    return triggerMidX > windowMidX ? 'right' : 'left';
};

// if expectCollisionHandled is true, we assume the popup component will handle
// collisions with the viewport bounds by having the popup render on the
// opposite side of the trigger, and do not try to avoid this scenario.
export const getPopupMaxHeight = (
    triggerElem: HTMLElement | null,
    windowInnerHeight: number,
    expectCollisionHandled: boolean
) => {
    if (!triggerElem) {
        return defaultPopupMaxHeight;
    }

    const { top, bottom } = triggerElem.getBoundingClientRect();
    const spaceBelow = Math.floor(windowInnerHeight - bottom);
    const spaceAbove = expectCollisionHandled ? top : 0;
    const maxHeight = Math.max(0, spaceBelow, spaceAbove);

    return `calc(${maxHeight}px - ${popupBottomPadding})`;
};

export type DropdownDataSource<TData> = Readonly<{
    /**
     * Items for selection.
     */
    items: TData[] | undefined;
    /**
     * If items are currently being loaded.
     */
    isLoading: boolean;
    /**
     * If there are more items available when the user
     * reaches the bottom of the item selections.
     */
    hasMore: boolean;
    /**
     * Callback will be called to request more items
     * if `hasMore` is `true` and `isLoading` is `false`.
     */
    onLoadMore: () => void;
}>;

export type DropdownFilterConfig = Readonly<{
    /**
     * Aria label for filter input.
     */
    ariaLabel?: string;
    /**
     * Optional placeholder text for the filter.
     */
    placeholder: string;
    /**
     * The current value of the filter.
     */
    filterText: string;
    /**
     * Callback to handle when the filter text changes.
     */
    onChange: (filterText: string) => void;
    /**
     * Max input length.
     */
    maxLength?: number;
}>;

export type DropdownResultRenderInfo<TData> = Readonly<{
    /**
     * The item value.
     */
    item: TData;
    /**
     * Ref associated with the item (used for keyboard selection and auto scroll).
     */
    itemRef: RefCallback<HTMLElement>;
    /**
     * If the item is currently highlighted.
     */
    isHighlighted: boolean;
    /**
     * Callback for if item is selected.
     */
    onSelect: () => void;
    /**
     * Callback for when the item's onPointerEnter & onPointerMove
     */
    onHover: React.PointerEventHandler;
}>;

export type DropdownResultsRenderer<TData> = (
    items: DropdownResultRenderInfo<TData>[]
) => ReactNode;

export type DropdownPopupProps<TData> = Readonly<{
    /**
     * Aria label of the popup.
     */
    ariaLabel?: string;
    /**
     * Ref for the popup.
     */
    popupRef: Ref<HTMLDivElement>;
    /**
     * Ref for the element the popup is anchored to.
     */
    anchorRef: RefObject<HTMLElement>;
    /**
     * The data to be rendered (can be an array or a dropdown data source object).
     */
    data: TData[] | DropdownDataSource<TData> | undefined;
    /**
     * Callback for rendering dropdown results.
     */
    renderResults: DropdownResultsRenderer<TData>;
    /**
     * The index of the currently highlighted result.
     */
    highlightedIndex: HighlightedIndex;
    /**
     * Callback for when a result is selected.
     */
    onSelect: (item: TData) => void;
    /**
     * Callback for when escape key is pressed.
     */
    onEscape?: () => void;
    /**
     * Callback for when the index of the currently highlighted result changes.
     */
    onHighlightedIndexChange: (index: HighlightedIndex) => void;
    /**
     * If the results component is disabled.
     */
    isDisabled?: boolean;
    /**
     * Filtering config.
     */
    filterable?: DropdownFilterConfig;
    /**
     * Callback for when popup is closed.
     */
    onClose: () => void;
    /**
     * Width of popup.
     */
    width?: string;
    /**
     * Max height of results.
     * @default '60vh';
     */
    maxHeight?: string;
    /**
     * Defines the container to which the popup will be appended. Defaults to 'body'
     */
    appendTo?: HTMLElement | null;
    /**
     * Optional item to add to the end of the list. Can be used to allow adding an item not in the list.
     */
    TrailingItemComponent?: TrailingItem;
}>;

export type TrailingItem = (props: {
    /**
     * Ref associated with the item (used for keyboard selection and auto scroll).
     */
    itemRef: React.Ref<HTMLLIElement>;
    /**
     * If the item is currently highlighted.
     */
    isHighlighted: boolean;
    onHover: (e: React.PointerEvent) => void;
    onClick: () => void;
}) => JSX.Element;

function getData<TData>(data: DropdownPopupProps<TData>['data']) {
    return !Array.isArray(data)
        ? {
              items: data?.items,
              isLoading: data?.isLoading ?? false,
              hasMore: data?.hasMore ?? false,
              onLoadMore: data?.onLoadMore,
          }
        : {
              items: data,
              isLoading: false,
              hasMore: false,
              onLoadMore: undefined,
          };
}

export function DropdownPopup<TData>({
    ariaLabel,
    popupRef,
    anchorRef,
    filterable,
    data,
    renderResults,
    highlightedIndex,
    onHighlightedIndexChange,
    onClose,
    onSelect,
    isDisabled,
    width,
    maxHeight = defaultPopupMaxHeight,
    appendTo,
    TrailingItemComponent,
}: DropdownPopupProps<TData>) {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isFullScreen = useIsViewportAtMost(MediaSize.xsmall);
    const { items, isLoading, hasMore, onLoadMore } = getData(data);
    const resultRefs = useRef<(HTMLElement | null)[]>([]);
    const trailingItemRef = useRef<HTMLLIElement>(null);

    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;

    const onHighlightedIndexChangeRef = useRef(onHighlightedIndexChange);
    onHighlightedIndexChangeRef.current = onHighlightedIndexChange;

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const onFilterKeyDown = useHighlightedIndex<TData>({
        items,
        highlightedIndex:
            highlightedIndex === 'after' ? items?.length ?? 0 : highlightedIndex,
        onChange: (idx, boundsMeta) => {
            if (boundsMeta === 'afterLast') {
                onHighlightedIndexChange(items?.length ?? 0);
                trailingItemRef.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'nearest',
                    inline: 'nearest',
                });
                return;
            }

            onHighlightedIndexChange(idx);
            scrollToHighlightedItem();
        },
        onSelect,
        isDisabled,
    });

    const highlightedIndexRef = useRef(highlightedIndex);
    highlightedIndexRef.current = highlightedIndex;
    const buildOnHover = useCallback(
        (index: HighlightedIndex) => (e: React.PointerEvent) => {
            // This hover callback enables:
            // 1) mouse movement to update the highlighted index
            // 2) avoiding the highlighted index being reset based on
            //    the mouse position when the user triggers a scroll
            //    without moving the mouse (e.g. by navigating with the
            //    keyboard or scrollwheel)
            //    ---
            // We are hooking both `pointermove` and `pointerenter`
            // because some events seem to get missed when using just
            // one of the two, leading to the item under the mouse not
            // being highlighted being a fairly common occurrence.
            const threshold = 0.001;

            if (
                e.type === 'pointermove' &&
                e.movementX ** 2 + e.movementY ** 2 < threshold
            ) {
                return;
            }

            if (e.type === 'pointerenter') {
                // Ignore `pointerenter` events if the pointer didn't
                // move. Such an event is likely caused by a scroll
                // (e.g. from keyboard or scroll wheel).
                // ---
                // `movement{X,Y}` do not seem to be populated for
                // pointerenter events (always 0), so we will track the
                // last known position of the pointer in a ref and do a
                // simple distSq check
                const thresholdSq = threshold ** 2;
                const { clientX: lastX, clientY: lastY } = lastPointerPositionRef.current;
                lastPointerPositionRef.current = e;
                if (
                    lastX ** 2 - e.clientX ** 2 + (lastY ** 2 - e.clientY ** 2) <
                    thresholdSq
                ) {
                    return;
                }
            }

            if (index === highlightedIndexRef.current) {
                return;
            }
            onHighlightedIndexChange(index);
            e.preventDefault();
            e.stopPropagation();
        },
        [onHighlightedIndexChange]
    );

    const lastPointerPositionRef = useRef({ clientX: 0, clientY: 0 });
    const resultsToRender = useMemo(
        () =>
            items?.map<DropdownResultRenderInfo<TData>>((item, index) => ({
                item,
                itemRef: (elem: HTMLInputElement) => {
                    resultRefs.current[index] = elem;
                },
                isHighlighted: index === highlightedIndex,
                onSelect: () => onSelectRef.current(item),
                onHover: buildOnHover(index),
            })) ?? [],
        [buildOnHover, highlightedIndex, items]
    );

    const renderedResults = useMemo(() => renderResults(resultsToRender), [
        resultsToRender,
        renderResults,
    ]);

    const { innerWidth: windowInnerWidth, innerHeight: windowInnerHeight } = window;

    const isFilterable = filterable !== undefined;

    const popupAlign = useMemo(
        () =>
            isFilterable ? getPopupAlign(anchorRef.current, windowInnerWidth) : undefined,
        [anchorRef, windowInnerWidth, isFilterable]
    );

    const adjustedMaxHeight = useMemo(
        () => getPopupMaxHeight(anchorRef.current, windowInnerHeight, true),
        [anchorRef, windowInnerHeight]
    );

    // Reset highlighted index when filter text changes
    useEffect(() => {
        if (filterable?.filterText) {
            onHighlightedIndexChangeRef.current(0);
        }
    }, [filterable?.filterText]);

    // Focus on filter input or first result when popup is opened
    useEffect(() => {
        window.setTimeout(() =>
            (searchInputRef.current ?? resultRefs.current[0])?.focus()
        );
    }, [searchInputRef]);

    // Scroll to highlighted item when popup is mounted
    const scrollToHighlightedItem = useCallback(
        (behavior: 'instant' | 'smooth' = 'instant') => {
            window.setTimeout(() => {
                if (highlightedIndexRef.current === 'after') {
                    trailingItemRef.current?.scrollIntoView({
                        block: 'center',
                        behavior,
                    });
                } else {
                    resultRefs.current[highlightedIndexRef.current]?.scrollIntoView({
                        block: 'center',
                        behavior,
                    });
                }
            });
        },
        []
    );

    const hasScrolledRef = useRef(false);
    useEffect(() => {
        if (!hasScrolledRef.current) {
            scrollToHighlightedItem();
            hasScrolledRef.current = true;
        }
    }, [scrollToHighlightedItem]);

    // Close popup on escape
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (!isDisabled && event.code === 'Escape') {
                onCloseRef.current();
                stopEvent(event);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isDisabled]);

    const clampToList = (val: number) =>
        Math.max(0, Math.min(items ? items.length - 1 : 0, val));

    const highlightedItem = () => {
        if (items === undefined) {
            return undefined;
        }
        return highlightedIndex === 'after'
            ? ([null, trailingItemRef.current] as const)
            : ([items[highlightedIndex], resultRefs.current[highlightedIndex]] as const);
    };

    const shouldRenderTrailingItem = !hasMore;
    useKeyPress('ArrowDown', (e) => {
        if (highlightedIndex === 'after') {
            return;
        }
        const next = highlightedIndex + 1;
        const nextClamped = clampToList(next);

        onHighlightedIndexChange(
            next !== nextClamped &&
                shouldRenderTrailingItem &&
                TrailingItemComponent !== undefined
                ? 'after'
                : nextClamped
        );

        stopEvent(e);
        scrollToHighlightedItem('smooth');
    });
    useKeyPress('ArrowUp', (e) => {
        if (items === undefined) {
            return;
        }
        onHighlightedIndexChange(
            clampToList(
                highlightedIndex === 'after' ? items.length - 1 : highlightedIndex - 1
            )
        );
        stopEvent(e);
        scrollToHighlightedItem('smooth');
    });

    useKeyPress('Enter', (e) => {
        if (items === undefined) {
            return;
        }
        const highlighted = highlightedItem();
        if (!highlighted) {
            return;
        }
        const [item, element] = highlighted;
        stopEvent(e);
        if (item) {
            onSelect(item);
        } else {
            element?.click();
        }
    });
    return (
        <StatelessPopup
            ariaLabel={ariaLabel}
            noPadding
            anchor={anchorRef.current}
            isFullscreen={isFullScreen}
            onClose={onClose}
            noOverlay
            ref={popupRef}
            popupClassName="modal-popup"
            anchorAlign={
                popupAlign ? { horizontal: popupAlign, vertical: 'bottom' } : undefined
            }
            popupAlign={
                popupAlign ? { horizontal: popupAlign, vertical: 'top' } : undefined
            }
            popupSettings={{
                style: {
                    width,
                },
            }}
            appendTo={appendTo}
        >
            <div
                className="position-relative"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    maxHeight: isFullScreen
                        ? undefined
                        : `max(8rem, min(${maxHeight}, ${adjustedMaxHeight}))`,
                    height: isFullScreen ? '100%' : undefined,
                    borderRadius: '0.5rem',
                }}
            >
                {filterable && (
                    <DebouncedSearchInput
                        ref={searchInputRef}
                        role="search"
                        aria-label={filterable.ariaLabel}
                        value={filterable.filterText}
                        onChange={filterable.onChange}
                        placeholder={filterable.placeholder}
                        onKeyDown={onFilterKeyDown}
                        maxLength={filterable.maxLength}
                        autoFocus
                    />
                )}
                <InfiniteScroll
                    isLoading={isLoading}
                    onLoadMore={onLoadMore}
                    enabled={!isLoading && hasMore}
                    style={{
                        position: 'relative',
                        flexGrow: isFullScreen ? 1 : undefined,
                        overflowY: 'auto',
                    }}
                >
                    <DropdownItemsList
                        role="menu"
                        //_something_ in the dropdown must be focusable,
                        // otherwise it insta onBlurs when you click the
                        // trigger. Normally, that should be the filter
                        // input, but if that doesn't exist, use the list
                        tabIndex={filterable ? undefined : 0}
                    >
                        {renderedResults}
                        {shouldRenderTrailingItem && TrailingItemComponent && (
                            <TrailingItemComponent
                                isHighlighted={highlightedIndex === 'after'}
                                itemRef={trailingItemRef}
                                onHover={buildOnHover('after')}
                                onClick={onClose}
                            />
                        )}
                    </DropdownItemsList>
                </InfiniteScroll>
                {isLoading && <SpinnerArea isLoading absolute />}
            </div>
        </StatelessPopup>
    );
}
